import { IconButton, Tooltip, Typography } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useEffect } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../redux/reducer/rootReducer";
import ServerAppGrid from "../app/ServerAppGrid";
import {
  useGetQuotationsQuery,
  useMarkQuotesAsReadMutation,
} from "../../redux/reducer/features/Quotation/quotationSlice";
import { AppFilter } from "../../models/app/AppModels";
import QuotationDetailDialog from "./Dialog/QuotationDetailDialog";
import { AppGridId } from "../../models/app/AppGridModels";
import {
  formatDate,
  GetAppGridColumns,
} from "../../services/utilities/utility";
import { addDealerDashboardCounts } from "../../redux/reducer/features/Dashboard/DashboardSlice";
import TruncatedCellWithTooltip from "../common/TruncatedCellWithTooltip";

interface QuotationGridProps {}
const QuotationGrid: React.FunctionComponent<QuotationGridProps> = (props) => {
  const dispatch = useDispatch();
  const stats = useSelector(
    (state: AppState) => state.dashboardReducer.dealerDashboard,
  );
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.Quotations,
    ),
  );
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
  });
  const [quotations, setQuotations] = React.useState<any[]>([]);
  const { data, isLoading, refetch, isFetching } = useGetQuotationsQuery({
    paging: paging,
  });
  const [markAsReadMutation, markAsReadMutationResult] =
    useMarkQuotesAsReadMutation();
  const [quotationsCount, setQuotationsCount] = React.useState<number>(0);
  const [quationDialog, setQuationDialog] = React.useState<{
    isOpen: boolean;
    quotation: any;
  }>({ isOpen: false, quotation: undefined });

  useEffect(() => {
    if (stats && stats.unreadQuotes > 0) {
      markAsReadMutation().then((res: any) => {
        if (res && res.data) {
          dispatch(
            addDealerDashboardCounts({
              unreadOrders: stats.unreadOrders,
              unreadQuotes: 0,
            }),
          );
        }
      });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [paging]);

  React.useEffect(() => {
    if (data && data.entityList) {
      setQuotations(data.entityList);
      setQuotationsCount(data.totalRecords);
    }
  }, [data]);
  const columns: GridColDef[] = [
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const firstName =
          params.row?.message !== "" ? params.row?.firstName : "N/A";
        return <TruncatedCellWithTooltip value={firstName} />;
      },
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 1,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const lastName =
          params.row?.message !== "" ? params.row?.lastName : "N/A";
        return <TruncatedCellWithTooltip value={lastName} />;
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.email} />;
      },
    },

    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      editable: false,
    },
    {
      field: "dmsLocation",
      headerName: "Location",
      flex: 1,
      editable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const location =
          params.row?.dmsLocation !== null
            ? params.row?.dmsLocation.name
            : "N/A";
        return <TruncatedCellWithTooltip value={location} />;
      },
    },
    {
      field: "message",
      headerName: "Message",
      flex: 1,
      editable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const message =
          params.row?.message !== "" ? params.row?.message : "N/A";
        return <TruncatedCellWithTooltip value={message} />;
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      editable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const title =
          params.row?.productAggregate &&
          params.row?.productAggregate?.title !== ""
            ? params.row?.productAggregate?.title
            : (params.row?.productTitle ?? "N/A");
        return <TruncatedCellWithTooltip value={title} />;
      },
    },
    {
      field: "price",
      headerName: "Price($)",
      headerAlign: "right",
      flex: 1,
      editable: false,
      align: "right",
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.productAggregate &&
        params.row?.productAggregate?.price !== ""
          ? params.row?.productAggregate?.price
          : "N/A",
    },
    {
      field: "createdAt",
      headerName: "Date",
      type: "actions",
      width: 230,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.createdAt ? formatDate(params.row.createdAt) : "N/A";
      },
    },
    {
      field: "actions",
      headerName: "Action",
      type: "actions",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <IconButton
              aria-label="viewOrder"
              size="small"
              onClick={() => {
                setQuationDialog({
                  isOpen: true,
                  quotation: params.row,
                });
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </>
        );
      },
    },
  ];
  return (
    <>
      <ServerAppGrid
        gridId={AppGridId.Quotations}
        data={quotations.map((n, i) => ({ id: i, ...n }))}
        columns={GetAppGridColumns(columns, gridSetting?.columnVisibilityModel)}
        isLoading={isLoading || isFetching}
        paging={paging}
        setPaging={setPaging}
        totalRecords={quotationsCount}
        height={"75vh"}
        hideCheckBoxes={true}
      ></ServerAppGrid>
      <QuotationDetailDialog
        quotation={quationDialog?.quotation}
        isOpen={quationDialog?.isOpen ?? false}
        onClose={() => {
          setQuationDialog({ isOpen: false, quotation: undefined });
        }}
      ></QuotationDetailDialog>
    </>
  );
};

export default QuotationGrid;
